import { normalizeLink } from '@/lib/storefront/normalizeLink'
import { normalizeCategoryPathOrShop } from '@/lib/storefront/categoryUrl'

/**
 * Turn layout/block links into absolute `/ref/:slug/...` paths so they do not resolve
 * relative to the current URL (e.g. `about` on `/ref/x/category/foo` → `/ref/x/category/about`).
 */
export function resolveRefStorefrontNavLink(
  link: unknown,
  storeSlug: string | undefined,
  storefrontPathPrefix = '',
): string {
  const withPrefix = (out: string) =>
    storefrontPathPrefix && out.startsWith('/ref/') ? `${storefrontPathPrefix}${out}` : out

  const trimmed = normalizeLink(link).trim()
  if (!trimmed) return storeSlug?.trim() ? withPrefix(`/ref/${storeSlug.trim()}`) : '/'
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('mailto:') || trimmed.startsWith('tel:')) {
    return trimmed
  }
  if (trimmed.startsWith('#')) return trimmed

  const hashIdx = trimmed.indexOf('#')
  const beforeHash = hashIdx >= 0 ? trimmed.slice(0, hashIdx) : trimmed
  const hash = hashIdx >= 0 ? trimmed.slice(hashIdx) : ''

  const qIdx = beforeHash.indexOf('?')
  const pathOnly = (qIdx >= 0 ? beforeHash.slice(0, qIdx) : beforeHash).trim()
  const query = qIdx >= 0 ? beforeHash.slice(qIdx) : ''

  // Harden category routes: /category, /category/, /category//, /category/undefined, /category/null.
  const normalizedCategoryPath = normalizeCategoryPathOrShop(pathOnly)
  if (normalizedCategoryPath) {
    if (!storeSlug?.trim()) return `${normalizedCategoryPath}${query}${hash}`
    return withPrefix(`/ref/${storeSlug.trim()}${normalizedCategoryPath}${query}${hash}`)
  }

  if (!storeSlug?.trim()) {
    return `${pathOnly}${query}${hash}`
  }

  const base = `/ref/${storeSlug.trim()}`

  if (pathOnly.startsWith('/ref/')) {
    return withPrefix(`${pathOnly}${query}${hash}`)
  }

  let path = pathOnly
  if (path.startsWith('/')) {
    path = path.slice(1)
  }

  if (!path) {
    return withPrefix(`${base}${query}${hash}`)
  }

  return withPrefix(`${base}/${path}${query}${hash}`)
}
